import React, { useState, useEffect } from 'react';
import { addAction } from '@shared/utils/logger';
import { distanceInMiles } from '@shared/utils/distanceInMiles';
import { useGoogleMaps } from '@shared/core/google-maps/useGoogleMaps';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { TravelMode, DirectionsStatus } from '@shared/core/google-maps/constants';

type WithDirectionResultProps<Origin extends google.maps.LatLngLiteral = google.maps.LatLngLiteral> = DeepReadonly<{
  originList: ReadonlyArray<Origin>;
  destination: google.maps.LatLngLiteral;
  hideDirection?: boolean;
}>;

export function withDirectionsResult<Origin extends google.maps.LatLngLiteral, T extends WithDirectionResultProps<Origin>>(
  WrappedComponent: React.ComponentType<T & { directions: google.maps.DirectionsResult | null; loading: boolean }>,
) {
  const WithDirectionService: React.FC<T> = props => {
    const [googleMaps, { loading: loadingGoogleMaps }] = useGoogleMaps({ loadOnMount: true });
    const [isDirectionsApiEnabled] = useIsFeatureEnabled('directionsApiEnabled');

    const { originList, destination, hideDirection: hideDirectionProps } = props;
    const hasDestination = destination.lat !== 0 && destination.lng !== 0;
    const hideDirection = !hasDestination || hideDirectionProps || !isDirectionsApiEnabled;

    const [loading, setLoading] = useState(false);
    const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
    const [lastAttemptKey, setLastAttemptKey] = useState<string | null>(null);

    useEffect(() => {
      if (!googleMaps || loadingGoogleMaps) return;
      const directionsService = new googleMaps.DirectionsService();
      const origin = originList[0];
      if (!origin?.lat || !origin?.lng || !destination.lat || !destination.lng) return;

      const requestKey = `${origin.lat},${origin.lng}:${destination.lat},${destination.lng}`;
      const hasAttempted = lastAttemptKey === requestKey;
      const milesToVenue = distanceInMiles(origin, destination);

      if (originList.length > 0 && destination && !directions && !loading && !hideDirection && milesToVenue && milesToVenue < 500 && !hasAttempted) {
        setLoading(true);
        const request = {
          origin: { lat: origin.lat, lng: origin.lng },
          destination: { lat: destination.lat, lng: destination.lng },
          travelMode: TravelMode.DRIVING,
        };

        try {
          directionsService.route(request, (result, status) => {
            if (status === DirectionsStatus.OK) {
              setDirections(result);
              setLoading(false);
              addAction('directionMapSuccess');
            } else {
              setLoading(false);
              setLastAttemptKey(requestKey);
              addAction('directionMapError', { status, request });
            }
          });
        } catch (e) {
          setLoading(false);
          setLastAttemptKey(requestKey);
          const message = e instanceof Error ? e.message : '';
          addAction('directionMapError', { status: 'Failed', message, request });
        }
      }
    }, [originList, destination, directions, loading, hideDirection, loadingGoogleMaps, googleMaps, isDirectionsApiEnabled, lastAttemptKey]);

    return (
      <WrappedComponent
        {...props}
        directions={directions}
        loading={loading}
      />
    );
  };

  return WithDirectionService;
}
